import { useAuth } from '@app/core/auth/authentication/hooks/useAuth';
import { useTimezone } from '@app/core/hooks/useTimezone';
import { ListView } from '@app/core/list-view/ListView';
import { ListViewQueryParams } from '@app/core/list-view/types';
import { auditReports } from '@mi-front/api-clients';
import { Paginated, Report } from '@mi-front/core';
import { apiClient } from '../../../../../../../api';
import { SavedReportListType } from '../../../../reports/constants';
import { SAVED_REPORTS_GIP_FILTERS_CONFIG } from '../filters/saved-reports-list-filters-config';
import { SavedReportListItem } from '../types';
import {
  savedReportsGipFiltersToApi,
  savedReportsGipSortToApi,
} from './map-saved-reports-gip-query';
import { SavedReportsGipTable } from './SavedReportsGipTable';

interface Props {
  /** GIP or GIP alt report type from the path chip. */
  reportType: Extract<SavedReportListType, 'AUDIT_GIP_STATUS' | 'AUDIT_GIP_ALTERNATIVE_REPORT'>;
}

/**
 * Maps a legacy `/reports` row into the shared list shape.
 */
function mapLegacyReport(report: Report): SavedReportListItem {
  const gipLike = report as unknown as auditReports.GipReportDto | auditReports.GipAltReportDto;
  const gipExportStatus = gipLike?.data?.export?.status;

  return {
    id: report.id,
    name: report.name,
    type: report.type,
    createdAt: String(report.createdAt),
    createdBy: report.createdBy ?? null,
    gipExportStatus: gipExportStatus ? String(gipExportStatus) : undefined,
  };
}

/**
 * GIP / GIP alt saved reports using ListView + nuqs filters.
 */
export function SavedReportsGipList({ reportType }: Props) {
  const auth = useAuth();
  const timezone = useTimezone();

  const queryFn = async (params: ListViewQueryParams): Promise<Paginated<SavedReportListItem>> => {
    const { nameLike, createdAtFrom, createdAtTo, onlyMine } = savedReportsGipFiltersToApi(
      params.filters,
      timezone,
    );

    const page = await apiClient.reports.fetchReports({
      type: reportType,
      createdById: onlyMine ? auth.user.id : undefined,
      nameLike,
      createdAtFrom,
      createdAtTo,
      page: params.pageIndex,
      pageSize: params.pageSize,
      sort: savedReportsGipSortToApi(params.sort),
    });

    return {
      results: page.results.map(mapLegacyReport),
      metadata: page.metadata,
    };
  };

  return (
    <ListView
      listId={`saved-reports-${reportType}`}
      filters={SAVED_REPORTS_GIP_FILTERS_CONFIG}
      queryFn={queryFn}
      ListComponent={SavedReportsGipTable}
    />
  );
}
